// static/src/router/index.js

import { createRouter, createWebHistory } from 'vue-router';
import { useUserStore } from '../stores/userStore';
import { useContentStore } from '../stores/contentStore';

// ============================================================================
// LAZY-LOADED PAGE COMPONENTS
// ============================================================================
//
// Real bug reported live 2026-09-10 (owner: clicking a nav link changes the
// URL, e.g. to /affiliates, but the page under the header is blank; a
// manual refresh fixes it, then it recurs "after a while"). Root cause:
// every deploy in this project replaces the entire static/dist/assets/
// directory with newly content-hashed filenames (confirmed repeatedly this
// same session -- WhiteLabelSettings-B8Gxq2J2s99o.js, then -CkbGzOmBeMbm.js,
// a different hash on every single build). A browser tab left open across
// a deploy still has the OLD hashed chunk URL for any route it hasn't
// visited yet baked into its already-loaded JS -- clicking to that route
// fires a dynamic import() for a file that's been deleted from the server,
// which 404s. The existing safety nets (router.onError below,
// onErrorCaptured in App.vue) exist for THIS exact class of failure, but
// don't reliably catch it: a rejected route-level async component loader
// can leave <router-view>'s resolved Component silently stale/undefined
// instead of throwing somewhere either of those listeners is guaranteed to
// see -- matching the reported symptom exactly (URL changes, content does
// not, no visible error message either).
//
// lazyLoad() catches the rejection at the one point it's unambiguous --
// the import() call itself -- and forces a single full page reload, which
// fetches the current index.html + manifest and resolves it. Guarded by
// sessionStorage per-pathname so a genuinely broken/missing chunk (not
// just a stale one) reloads at most once before falling through to the
// existing error UI, instead of reload-looping forever.
function lazyLoad(loader) {
  return () => {
    const key = 'lr_chunk_reload:' + location.pathname;
    return loader().then((mod) => {
      // Clear the retry guard on a real success -- otherwise a LATER,
      // genuinely new chunk-load failure on this same path (a future
      // deploy, say) would be silently skipped past the one-time reload
      // and go straight to the error UI, just because an earlier,
      // unrelated failure on the same path already used up the retry
      // within this browser tab's session.
      try { sessionStorage.removeItem(key); } catch (_e) { /* ignore */ }
      return mod;
    }).catch((err) => {
      const msg = String(err?.message || err || '');
      const isChunkLoadError = /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Loading chunk|Loading CSS chunk/i.test(msg);
      if (isChunkLoadError) {
        try {
          if (!sessionStorage.getItem(key)) {
            sessionStorage.setItem(key, '1');
            window.location.reload();
            return new Promise(() => {}); // reload is already in flight -- never settle this one
          }
        } catch (_e) { /* sessionStorage unavailable (private mode etc.) -- fall through to rethrow */ }
      }
      throw err; // not a chunk error, or already retried once -- let the existing error UI handle it
    });
  };
}

const HomePage           = lazyLoad(() => import('../components/pages/HomePage.vue'));
const LoginPage          = lazyLoad(() => import('../components/pages/LoginPage.vue'));
const RegisterPage       = lazyLoad(() => import('../components/pages/RegisterPage.vue'));
const DashboardPage      = lazyLoad(() => import('../components/pages/DashboardPage.vue'));
const ProfilePage        = lazyLoad(() => import('../components/pages/ProfilePage.vue'));
const ReferralPage       = lazyLoad(() => import('../components/pages/ReferralPage.vue'));
const SettingsPage       = lazyLoad(() => import('../components/pages/SettingsPage.vue'));
const HistoryPage        = lazyLoad(() => import('../components/pages/HistoryPage.vue'));
const AnalyzePage        = lazyLoad(() => import('../components/pages/AnalyzePage.vue'));
const ReportPage         = lazyLoad(() => import('../components/pages/ReportPage.vue'));
const CompareReportPage  = lazyLoad(() => import('../components/pages/CompareReportPage.vue'));
const SharedReportPage   = lazyLoad(() => import('../components/pages/SharedReportPage.vue'));
const ProcessingPage     = lazyLoad(() => import('../components/pages/ProcessingPage.vue'));
const FeaturesPage       = lazyLoad(() => import('../components/pages/FeaturesPage.vue'));
const GeoOptimizationPage = lazyLoad(() => import('../components/pages/GeoOptimizationPage.vue'));
// Free GEO checker landing page (2026-09-18, "option A"): one-input,
// low-commitment entry to the SAME public-audit funnel as the homepage box.
// Framed as "check your AI-search visibility" rather than "run a 100+ point
// audit" -- a 10-second ask instead of a big one.
const GeoCheckerPage = lazyLoad(() => import('../components/pages/GeoCheckerPage.vue'));
const SeoOptimizationPage = lazyLoad(() => import('../components/pages/SeoOptimizationPage.vue'));
const PricingPage        = lazyLoad(() => import('../components/pages/PricingPage.vue'));
const AboutPage          = lazyLoad(() => import('../components/pages/AboutPage.vue'));
const LearnPage          = lazyLoad(() => import('../components/pages/LearnPage.vue'));
const ArticlePage        = lazyLoad(() => import('../components/pages/ArticlePage.vue'));
const AdminBlogPage      = lazyLoad(() => import('../components/pages/AdminBlogPage.vue'));
const AdminEnterprisePage = lazyLoad(() => import('../components/pages/AdminEnterprisePage.vue'));
const AdminCoolingOffPage = lazyLoad(() => import('../components/pages/AdminCoolingOffPage.vue'));
const AffiliatePage     = lazyLoad(() => import('../components/pages/AffiliatePage.vue'));
const AffiliateApplyPage = lazyLoad(() => import('../components/pages/AffiliateApplyPage.vue'));
const AffiliateDashboard = lazyLoad(() => import('../components/pages/AffiliateDashboard.vue'));
const ContactPage        = lazyLoad(() => import('../components/pages/ContactPage.vue'));
const ForgotPasswordPage = lazyLoad(() => import('../components/pages/ForgotPasswordPage.vue'));
const ResetPasswordPage  = lazyLoad(() => import('../components/pages/ResetPasswordPage.vue'));
// ============================================================================
// NEW: Prospecting Page (Lead Generation)
// ============================================================================
const ProspectingPage = lazyLoad(() => import('../components/pages/ProspectingPage.vue'));
// ============================================================================
// NEW: Campaign Pages
// ============================================================================
const CampaignsPage       = lazyLoad(() => import('../components/pages/CampaignsPage.vue'));
const CampaignDetailPage  = lazyLoad(() => import('../components/pages/CampaignDetailPage.vue'));
// ============================================================================
// NEW: White-Label Branding Page
// ============================================================================
const WhiteLabelSettingsPage = lazyLoad(() => import('../components/pages/WhiteLabelSettings.vue'));
const LegalPage              = lazyLoad(() => import('../components/pages/LegalPage.vue'));
const NotFoundPage           = lazyLoad(() => import('../components/pages/NotFoundPage.vue'));
// ============================================================================
// ROUTE DEFINITIONS
// ============================================================================

const routes = [
  // ===== PUBLIC ROUTES =====
  { path: '/',                 name: 'home',            component: HomePage,           meta: { title: 'Home' } },
  { path: '/features',         name: 'features',        component: FeaturesPage,       meta: { title: 'Features' } },
  { path: '/geo-checker',      name: 'geo-checker',     component: GeoCheckerPage,     meta: { title: 'Free GEO Checker' } },
  { path: '/geo-optimization', name: 'geo-optimization', component: GeoOptimizationPage, meta: { title: 'GEO Optimization' } },
  { path: '/seo-optimization', name: 'seo-optimization', component: SeoOptimizationPage, meta: { title: 'SEO Optimization' } },
  { path: '/pricing',          name: 'pricing',         component: PricingPage,        meta: { title: 'Pricing' } },
  { path: '/about',            name: 'about',           component: AboutPage,          meta: { title: 'About' } },
  { path: '/learn',            name: 'learn',           component: LearnPage,          meta: { title: 'Learn' } },
  { path: '/learn/:slug',      name: 'article',         component: ArticlePage,        meta: { title: 'Article' } },
  { path: '/contact',          name: 'contact',         component: ContactPage,        meta: { title: 'Contact' } },
  { path: '/contact-sales',    name: 'contact-sales',   component: ContactPage,        meta: { title: 'Contact Sales' } },
  { path: '/privacy',          name: 'privacy',         component: LegalPage,          meta: { title: 'Privacy Policy', legalType: 'privacy' } },
  { path: '/terms',            name: 'terms',           component: LegalPage,          meta: { title: 'Terms of Service', legalType: 'terms' } },
  { path: '/service-level-agreement', name: 'sla',      component: LegalPage,          meta: { title: 'Service Level Agreement', legalType: 'sla' } },

  // ===== AFFILIATE PROGRAM (public) =====
  { path: '/affiliates',        name: 'affiliates',      component: AffiliatePage,      meta: { title: 'Partner Program' } },
  { path: '/affiliates/apply',  name: 'affiliates-apply',component: AffiliateApplyPage, meta: { title: 'Apply - Partner Program', requiresAuth: true } },
  { path: '/affiliates/dashboard', name: 'affiliates-dashboard', component: AffiliateDashboard, meta: { title: 'Partner Dashboard', requiresAuth: true } },
  
  // ===== GUEST-ONLY ROUTES (redirect to dashboard if authenticated) =====
  { path: '/login',            name: 'login',           component: LoginPage,          meta: { title: 'Login', guest: true } },
  { path: '/register',         name: 'register',        component: RegisterPage,       meta: { title: 'Register', guest: true } },
  { path: '/forgot-password',  name: 'forgot-password', component: ForgotPasswordPage, meta: { title: 'Forgot Password', guest: true } },
  // Real bug found 2026-08-28: marked guest:true like the other auth
  // pages, so router.beforeEach's isGuestOnly redirect bounced ANY
  // already-authenticated browser straight to /dashboard the moment
  // it hit this route, silently discarding the ?token= in the URL --
  // a real reset link became unusable for anyone with an existing
  // session (stale, a different account, whatever). Unlike
  // login/register/forgot-password, this page's whole purpose is
  // driven by a per-request token identifying a specific account to
  // reset, independent of whatever session happens to already be
  // active -- the backend's POST /api/reset-password is purely
  // token-based too (confirmed: no auth/session dependency at all),
  // so there's no reason to gate the page on being logged out.
  { path: '/reset-password',   name: 'reset-password',  component: ResetPasswordPage,  meta: { title: 'Reset Password' } },
  
  // ===== AUTHENTICATED ROUTES =====
  { path: '/dashboard',        name: 'dashboard',       component: DashboardPage,      meta: { title: 'Dashboard', requiresAuth: true } },
  { path: '/referrals',        name: 'referrals',       component: ReferralPage,       meta: { title: 'Referrals', requiresAuth: true } },
  { path: '/settings',         name: 'settings',        component: SettingsPage,       meta: { title: 'Settings', requiresAuth: true } },
  { path: '/settings/white-label', name: 'white-label-settings', component: WhiteLabelSettingsPage, meta: { title: 'White-Label Branding', requiresAuth: true } },
  { path: '/history',          name: 'history',         component: HistoryPage,        meta: { title: 'History', requiresAuth: true } },
  // Deliberately NOT requiresAuth — /api/public/analyze already supports
  // anonymous submissions (auto-creates a guest_*@leadranks.io account
  // server-side), and CLAUDE.md's Starter-tier plan explicitly promises
  // "one manual audit... no signup required" for the free tier. Gating
  // this route bounced every logged-out visitor who clicked the homepage
  // audit CTA straight to /login before they saw any value — the opposite
  // of the intended funnel. AnalyzePage.vue already degrades gracefully
  // when /api/auth/me 401s (loadUserData()'s `if (response.ok)` guard).
  { path: '/analyze',          name: 'analyze',         component: AnalyzePage,        meta: { title: 'Analyze' } },
  
  // ===== NEW: Prospecting (Lead Generation) =====
  { 
    path: '/prospecting',      
    name: 'prospecting',       
    component: ProspectingPage,    
    meta: { 
      title: 'Prospecting', 
      requiresAuth: true,
      description: 'Find warm leads who need SEO help'
    } 
  },
  
  // ===== NEW: Campaigns =====
  {
    path: '/campaigns',
    name: 'campaigns',
    component: CampaignsPage,
    meta: { title: 'Campaigns', requiresAuth: true, description: 'Group searches and track leads per campaign' }
  },
  {
    path: '/campaigns/:id',
    name: 'campaign-detail',
    component: CampaignDetailPage,
    props: true,
    meta: { title: 'Campaign Detail', requiresAuth: true }
  },
  
  // ===== DYNAMIC ROUTES =====
  // Deliberately NOT requiresAuth — see the /analyze route comment above.
  // A guest's own analysis returns 200 from GET /api/analysis/:id (backend's
  // is_guest_analysis bypass in get_analysis_data()); a real private report
  // still correctly 401s/403s, and ReportPage.vue's own fetch already
  // handles both (401 → router.push('/login?redirect=...'), 403 → a proper
  // ErrorState) — the router-level gate was strictly redundant for owners
  // and actively broke the no-signup guest flow. Do not re-add without
  // re-reading this — see CLAUDE.md anti-regression #10 for why it was
  // added in the first place (an UNHANDLED 403 at the time); that gap is
  // closed at the page level now, so the blanket route gate is no longer
  // the right tool.
  { path: '/report/:analysisId',   name: 'report',      component: ReportPage,         props: true, meta: { title: 'SEO Report' } },
  { path: '/report/:analysisId/compare/:competitorId', name: 'report-compare', component: CompareReportPage, props: true, meta: { title: 'Compare Reports', requiresAuth: true } },
  // Real bug fixed 2026-08-29 (owner-reported live: a "/r/{token}" report
  // link landed on the plain homepage instead of the report). This route
  // was PERMANENTLY unreachable in production, not just occasionally
  // broken: app.py registers a real server-side FastAPI route at the
  // exact same "/r/{ref_code}" path for affiliate-click tracking
  // (src/api/affiliate_endpoints.py's track_click) -- a backend route
  // always intercepts a request before it ever reaches the SPA shell
  // this Vue Router route lives in. Every report link built with this
  // prefix (src/api/white_label.py's send-report-to-client email flow,
  // now fixed to use /shared/{token} instead) was silently swallowed by
  // the affiliate handler, which found no matching ref_code and
  // redirected straight to "/". Removed rather than left as dead-but-
  // harmless: a route that LOOKS like it works but can never actually
  // fire in production is worse than no route, since it invites the
  // exact mistake that caused this bug. /shared/:token is the one real,
  // working, already-tested route (see CLAUDE.md's own session history)
  // -- keep using that, don't reintroduce /r/:token for report shares.
  { path: '/shared/:token',        name: 'shared-report', component: SharedReportPage, props: true, meta: { title: 'SEO Report', hideNav: true } },
  { path: '/processing/:analysisId', name: 'processing', component: ProcessingPage,     props: true, meta: { title: 'Processing' } },
  
  // ===== ADMIN ROUTES =====
  { path: '/admin/blog', name: 'admin-blog', component: AdminBlogPage, meta: { title: 'Blog Admin', requiresAuth: true } },
  { path: '/admin/enterprise', name: 'admin-enterprise', component: AdminEnterprisePage, meta: { title: 'Enterprise Deal', requiresAuth: true } },
  { path: '/admin/cooling-off', name: 'admin-cooling-off', component: AdminCoolingOffPage, meta: { title: 'Cooling-Off Eligibility', requiresAuth: true } },

  // ===== CATCH-ALL =====
  { path: '/:pathMatch(.*)*', name: 'not-found', component: NotFoundPage, meta: { title: 'Page Not Found' } }
];

// ============================================================================
// ROUTER INSTANCE
// ============================================================================

// Factory (not just a module singleton) so core/prerender.py's in-browser
// SSR render (static/src/prerender-ssr.js) can build a SECOND, isolated
// router instance backed by an in-memory history — without touching the
// real browser URL/history of the prerender page. The singleton the app
// itself uses (below) is just this factory with the real web history.
export function createAppRouter(history) {
  const router = createRouter({
  history,
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition;
    if (to.hash) {
      // Poll for the target element to actually exist before resolving,
      // instead of a blind fixed delay -- a lazy-loaded route's chunk
      // (fetch + parse + mount + render) can easily take longer than a
      // fixed 100ms on a heavier page, silently scrolling nowhere once the
      // window passed without the element ever being found. Capped at ~2s
      // so a genuinely-missing anchor still resolves instead of hanging.
      return new Promise((resolve) => {
        let attempts = 0;
        const maxAttempts = 40; // 40 * 50ms = 2s cap
        const poll = () => {
          attempts++;
          if (document.querySelector(to.hash) || attempts >= maxAttempts) {
            resolve({ el: to.hash, behavior: 'smooth', top: 96 });
          } else {
            setTimeout(poll, 50);
          }
        };
        poll();
      });
    }
    return { top: 0 };
  }
});

// ============================================================================
// NAVIGATION GUARD
// ============================================================================

router.beforeEach(async (to, from, next) => {
  const userStore = useUserStore();
  
  try {
    // Fetch user if we haven't resolved auth state yet. (Previously checked
    // `!userStore.loading`, but `loading` defaults to true on boot — so this
    // was always false on a fresh page load and fetchUser() never ran,
    // bouncing every hard navigation to a requiresAuth route straight to
    // /login regardless of a valid session cookie. See userStore.js.)
    if (!userStore.initialized) {
      await userStore.fetchUser();
    }
  } catch (_err) {
    // Auth check failed — allow navigation but don't crash
    console.warn('Auth check failed during navigation, continuing as guest');
  }
  
  // Content prefetch for the data-driven marketing routes. Runs (and is
  // awaited) before the app mounts — main.js awaits router.isReady() — so the
  // SSR snapshot AND the client's first hydration render both start from the
  // same store state. Without this, /learn and /learn/:slug fetch in
  // onMounted, so the SSR pass would render a loading skeleton (losing real
  // content from the snapshot) and/or the client's first render would
  // disagree with a content-bearing snapshot. See stores/contentStore.js.
  try {
    const contentStore = useContentStore();
    if (to.name === 'learn') {
      await contentStore.ensureLearn();
    } else if (to.name === 'article' && to.params.slug) {
      await contentStore.ensureArticle(String(to.params.slug));
    }
  } catch (_err) {
    // Never let a content-prefetch hiccup block navigation; the page falls
    // back to its own fetch when it was not seeded.
    console.warn('Content prefetch failed during navigation, continuing');
  }

  // Check route requirements
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
  const isGuestOnly = to.matched.some(record => record.meta.guest);
  
  // Set page title
  if (to.meta.title) {
    document.title = `${to.meta.title} | LeadRanks`;
  }
  
  // Update meta description if provided
  if (to.meta.description) {
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', to.meta.description);
    }
  }
  
  // Authentication checks
  if (requiresAuth && !userStore.isAuthenticated) {
    // Redirect to login with return URL
    next({ path: '/login', query: { redirect: to.fullPath } });
  } else if (isGuestOnly && userStore.isAuthenticated) {
    // Redirect authenticated users away from guest pages
    next('/dashboard');
  } else {
    next();
  }
});

router.onError((err) => {
  // Any router error can wedge the internal state so all router-link
  // clicks are silently consumed. Try a replace first, then hard reload.
  const current = router.currentRoute.value?.fullPath || '/';
  router.replace(current).catch((err2) => {
    console.error('Router recovery failed — forcing reload:', err2?.message);
    window.location.reload();
  });
});

// ============================================================================
// POST-NAVIGATION: Track page views (optional analytics)
// ============================================================================

router.afterEach((to) => {
  // GA4 page_view, one per SPA navigation (including the first). The initial
  // gtag('config') deliberately sets send_page_view:false (see
  // static/src/analytics/ga4.js) so the landing page isn't counted twice.
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', 'page_view', {
      page_path: to.fullPath,
      page_location: window.location.href,
      page_title: to.meta.title || 'LeadRanks'
    });
  }
});

  return router;
}

const router = createAppRouter(createWebHistory());

export default router;